/* eslint-disable react-refresh/only-export-components */
import {
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import WorkflowNodeOptions from "./options";
import TooltipWrapper from "../Tooltip";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { useParams } from "react-router";
import { useUIStore } from "@/workflow_api/store/uiStore";

export const getNodeContent = (
  type: string,
): {
  icon: { type: string; name: string };
  label: string;
  labelType: "neutral" | "action" | "info" | "success" | "warning" | "danger";
} => {
  switch (type) {
    case NodeTypes.INITIALIZER:
      return {
        icon: {
          type: "fas",
          name: "play",
        },
        label: "Inicio",
        labelType: "neutral",
      };
    case NodeTypes.IF:
      return {
        icon: {
          type: "fas",
          name: "split",
        },
        label: "Operador If",
        labelType: "neutral",
      };
    case NodeTypes.ASSIGN_GROUP: {
      return {
        icon: {
          type: "fas",
          name: "users",
        },
        label: "Asignar grupo",
        labelType: "info",
      };
    }
    case NodeTypes.FORM:
      return {
        icon: {
          type: "fas",
          name: "bars",
        },
        label: "Formulario",
        labelType: "action",
      };
    case NodeTypes.SWITCH:
      return {
        icon: {
          type: "fas",
          name: "arrows-cross",
        },
        label: "Switch",
        labelType: "neutral",
      };
    case NodeTypes.CALCULATED: {
      return {
        icon: {
          type: "fas",
          name: "function",
        },
        label: "Calculado",
        labelType: "success",
      };
    }
    case NodeTypes.DATA_CENTER: {
      return {
        icon: {
          type: "fas",
          name: "table",
        },
        label: "Datos",
        labelType: "info",
      };
    }
    case NodeTypes.EXPEDIENTE: {
      return {
        icon: {
          type: "fas",
          name: "folder-open",
        },
        label: "Expediente",
        labelType: "info",
      };
    }
    case NodeTypes.CORRECTION: {
      return {
        icon: {
          type: "fas",
          name: "file-edit",
        },
        label: "Revisión",
        labelType: "action",
      };
    }
    case NodeTypes.DOC_GENERATOR: {
      return {
        icon: {
          type: "fas",
          name: "file-lines",
        },
        label: "Generador",
        labelType: "action",
      };
    }
    case NodeTypes.PAYMENT: {
      return {
        icon: {
          type: "fas",
          name: "dollar-sign",
        },
        label: "Pago",
        labelType: "warning",
      };
    }
    case NodeTypes.N8N: {
      return {
        icon: {
          type: "fas",
          name: "hexagon-nodes",
        },
        label: "N8N",
        labelType: "warning",
      };
    }
    case NodeTypes.SIGN_DOCUMENT: {
      return {
        icon: {
          type: "fas",
          name: "signature",
        },
        label: "Firma",
        labelType: "action",
      };
    }
    case NodeTypes.MULTITHREAD: {
      return {
        icon: {
          type: "fas",
          name: "chart-diagram",
        },
        label: "Multihilos",
        labelType: "neutral",
      };
    }
    case NodeTypes.NOTIFICATION: {
      return {
        icon: {
          type: "fas",
          name: "bell",
        },
        label: "Notificación",
        labelType: "info",
      };
    }
    case NodeTypes.WAIT: {
      return {
        icon: {
          type: "fas",
          name: "hourglass-half",
        },
        label: "Wait",
        labelType: "neutral",
      };
    }
    case NodeTypes.INTEROPERABILITY: {
      return {
        icon: {
          type: "fas",
          name: "diagram-nested",
        },
        label: "Interop.",
        labelType: "warning",
      };
    }
    case NodeTypes.RESOLUTION: {
      return {
        icon: {
          type: "fas",
          name: "file-circle-plus",
        },
        label: "Resolución",
        labelType: "action",
      };
    }
    case NodeTypes.MERGE: {
      return {
        icon: {
          type: "fas",
          name: "code-merge",
        },
        label: "Merge",
        labelType: "neutral",
      };
    }
    case NodeTypes.END:
      return {
        icon: {
          type: "fas",
          name: "stop",
        },
        label: "Final",
        labelType: "neutral",
      };
    default:
      return {
        icon: {
          type: "far",
          name: "cog",
        },
        label: "Custom Node",
        labelType: "neutral",
      };
  }
};

interface Props {
  id: string;
  type: string;
  displayName: string;
  isExecutionView?: boolean;
  executionStatus?: string;
}

export const NodeContent = ({
  id,
  type,
  displayName,
  isExecutionView,
  executionStatus,
}: Props) => {
  const params = useParams();
  const data = getNodeContent(type);
  const { setExecutionDetailsOpen, setSelectedNodeId, setNodeDetailsOpen } =
    useUIStore();

  const handleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNodeId(id);
    setNodeDetailsOpen(true);
  };

  return (
    <div
      className="w-full h-full relative group flex flex-col"
      onClick={() => {
        if (!isExecutionView) return;
        setExecutionDetailsOpen(true);
        setSelectedNodeId(id);
      }}
      onDoubleClick={(e) => {
        if (isExecutionView) return;
        if (type === NodeTypes.END) return;
        handleDetails(e);
      }}
    >
      {!isExecutionView && (
        <WorkflowNodeOptions nodeId={id} flowId={params.flowId!} />
      )}
      {executionStatus && (
        <div className="absolute -top-8 -left-1 z-10">
          <Badge variant="neutral" size="xs" className="rounded-lg px-1.5">
            {executionStatus}
          </Badge>
        </div>
      )}
      <div className="flex-1 w-full flex items-center justify-center bg-neutral-object rounded-t-sm rounded-bl-sm">
        <Avatar size="xl" shape="square">
          <AvatarFallback color={data.labelType} shape="square" type="icon">
            <Icon type={data.icon.type} name={data.icon.name} />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="w-full flex bg-neutral-object">
        <div className="flex-1 max-w-[104px] rounded-tr-lg bg-neutral-background pt-0.5 pr-1">
          <Badge
            variant={data.labelType}
            fill="solid"
            size="xs"
            className="w-full rounded-lg truncate px-1.5"
          >
            {data.label}
          </Badge>
        </div>
        <div className="bg-neutral-background w-full">
          <div className="h-full w-full rounded-b-lg bg-neutral-object">
            &nbsp;
          </div>
        </div>
        <TooltipWrapper title={displayName} position="bottom">
          <div className="absolute pt-8 text-center w-full text-body text-sm leading-2xl truncate overflow-hidden"></div>
        </TooltipWrapper>
      </div>
    </div>
  );
};
